import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { 
  ShoppingCart, 
  Package, 
  Wallet,
  Phone,
  LogOut,
  Plus,
  Edit,
  Check,
  AlertCircle,
  Calendar,
  TrendingUp,
  Minus,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerPortalPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState('');
  const [customer, setCustomer] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Order dialog state
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderDate, setOrderDate] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [orderItems, setOrderItems] = useState([]);
  const [orderNotes, setOrderNotes] = useState('');

  useEffect(() => {
    const savedToken = localStorage.getItem('customer_token');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchData();
    }
  }, [isLoggedIn, token]);

  const getAuthHeader = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/customer/login`, { username, password });
      const newToken = res.data.access_token;
      setToken(newToken);
      setCustomer(res.data.customer);
      localStorage.setItem('customer_token', newToken);
      setIsLoggedIn(true);
      toast.success('تم تسجيل الدخول بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تسجيل الدخول');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken('');
    setCustomer(null);
    localStorage.removeItem('customer_token');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { headers: getAuthHeader() };
      const [customerRes, productsRes, ordersRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/customer/me`, headers),
        axios.get(`${API_URL}/api/customer/products`, headers),
        axios.get(`${API_URL}/api/customer/orders`, headers),
        axios.get(`${API_URL}/api/customer/weekly-summary`, headers)
      ]);
      
      setCustomer(customerRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setWeeklySummary(summaryRes.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const openNewOrderDialog = () => {
    setEditingOrder(null);
    setOrderDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
    setOrderItems(products.map(p => ({ product_id: p.id, quantity: 0 })));
    setOrderNotes('');
    setOrderDialogOpen(true);
  };

  const openEditOrderDialog = (order) => {
    if (!order.can_edit) {
      toast.error('لا يمكن تعديل هذا الطلب');
      return;
    }
    setEditingOrder(order);
    setOrderDate(order.date);
    setOrderItems(products.map(p => {
      const existingItem = order.items.find(i => i.product_id === p.id);
      return { product_id: p.id, quantity: existingItem?.quantity || 0 };
    }));
    setOrderNotes(order.notes || '');
    setOrderDialogOpen(true);
  };

  const updateQuantity = (productId, newQty) => {
    setOrderItems(orderItems.map(i => 
      i.product_id === productId ? {...i, quantity: Math.max(0, newQty)} : i
    ));
  };

  const handleSaveOrder = async () => {
    const hasItems = orderItems.some(item => item.quantity > 0);
    if (!hasItems) {
      toast.error('الرجاء إضافة منتج واحد على الأقل');
      return;
    }

    try {
      const headers = { headers: getAuthHeader() };
      const itemsToSend = orderItems.filter(item => item.quantity > 0);
      
      if (editingOrder) {
        await axios.put(`${API_URL}/api/customer/orders/${editingOrder.id}`, { items: itemsToSend, notes: orderNotes }, headers);
        toast.success('تم تعديل الطلب بنجاح');
      } else {
        await axios.post(`${API_URL}/api/customer/orders`, { date: orderDate, items: itemsToSend, notes: orderNotes }, headers);
        toast.success('تم إضافة الطلب بنجاح');
      }
      
      setOrderDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في حفظ الطلب');
    }
  };

  const calculateOrderTotal = () => {
    return orderItems.reduce((total, item) => {
      const product = products.find(p => p.id === item.product_id);
      return total + (product?.price || 0) * item.quantity;
    }, 0);
  };

  const quickAmounts = [5, 10, 15, 30, 50];

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-stone-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-10 h-10 text-amber-600" />
            </div>
            <CardTitle className="text-2xl md:text-3xl">بوابة الزبون</CardTitle>
            <p className="text-stone-500 mt-2">سجل دخولك لإنشاء طلباتك</p>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-base">اسم المستخدم</Label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="أدخل اسم المستخدم"
                  className="h-12 text-lg"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base">كلمة المرور</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="أدخل كلمة المرور"
                  className="h-12 text-lg"
                  required
                />
              </div>
              <Button type="submit" className="w-full h-12 text-lg bg-amber-600 hover:bg-amber-700" disabled={loginLoading}>
                {loginLoading ? 'جاري التحميل...' : 'تسجيل الدخول'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Portal
  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-amber-600 text-white p-4 sticky top-0 z-10 shadow-md">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-xl font-bold">{customer?.name}</h1>
            <p className="text-amber-100 text-sm">{customer?.bakery_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-white hover:bg-amber-700">
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-amber-700">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Summary Cards - Desktop: Row, Mobile: Stack */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Weekly Summary */}
          {weeklySummary && (
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
              <CardContent className="p-4 md:p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5" />
                  ملخص الأسبوع
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/20 rounded-xl p-3 md:p-4">
                    <p className="text-amber-100 text-xs md:text-sm">إجمالي الأسبوع</p>
                    <p className="text-xl md:text-2xl font-bold">€{weeklySummary.weekly_total.toFixed(2)}</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 md:p-4">
                    <p className="text-amber-100 text-xs md:text-sm">صناديق الأسبوع</p>
                    <p className="text-xl md:text-2xl font-bold">{weeklySummary.weekly_boxes}</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 md:p-4">
                    <p className="text-amber-100 text-xs md:text-sm">المدفوع</p>
                    <p className="text-xl md:text-2xl font-bold">€{weeklySummary.weekly_paid.toFixed(2)}</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 md:p-4">
                    <p className="text-amber-100 text-xs md:text-sm">المتبقي</p>
                    <p className="text-xl md:text-2xl font-bold">€{weeklySummary.weekly_remaining.toFixed(2)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Account Balance */}
          {weeklySummary && (
            <Card className="border-2 border-stone-200">
              <CardContent className="p-4 md:p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2 text-lg">
                  <Wallet className="w-5 h-5 text-amber-600" />
                  الحساب الإجمالي
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-stone-50 rounded-lg">
                    <span className="text-stone-600">إجمالي المستحق</span>
                    <span className="font-bold text-lg">€{weeklySummary.total_debt.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                    <span className="text-emerald-700">إجمالي المدفوع</span>
                    <span className="font-bold text-lg text-emerald-600">€{weeklySummary.total_paid.toFixed(2)}</span>
                  </div>
                  <div className={`flex justify-between items-center p-4 rounded-lg ${weeklySummary.balance > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                    <span className="font-medium text-lg">الرصيد المتبقي</span>
                    <span className={`font-bold text-2xl ${weeklySummary.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      €{weeklySummary.balance.toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* New Order Button */}
        <Button 
          onClick={openNewOrderDialog} 
          className="w-full bg-emerald-600 hover:bg-emerald-700 h-14 md:h-16 text-lg md:text-xl font-bold shadow-lg"
        >
          <Plus className="w-6 h-6 md:w-7 md:h-7 me-2" />
          إنشاء طلب جديد
        </Button>

        {/* Contact Bakery */}
        {customer?.bakery_phone && (
          <a 
            href={`tel:${customer.bakery_phone}`}
            className="flex items-center justify-center gap-3 bg-white border-2 border-emerald-200 text-emerald-700 p-4 rounded-xl font-medium hover:bg-emerald-50 transition-colors"
          >
            <Phone className="w-5 h-5" />
            <span>للتواصل: {customer.bakery_phone}</span>
          </a>
        )}

        {/* Recent Orders */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg md:text-xl flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-amber-600" />
              طلباتي الأخيرة
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin w-10 h-10 border-4 border-amber-600 border-t-transparent rounded-full"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <ShoppingCart className="w-16 h-16 mx-auto text-stone-300 mb-3" />
                <p className="text-lg">لا توجد طلبات</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {orders.slice(0, 10).map((order) => (
                  <div 
                    key={order.id} 
                    className={`p-4 rounded-xl border-2 transition-all ${
                      order.created_by === 'customer' 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-white border-stone-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-stone-400" />
                        <span className="font-bold">{order.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {order.edited && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">معدّل</span>
                        )}
                        {order.created_by === 'customer' && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">بواسطتك</span>
                        )}
                        {order.delivery_status === 'full' && (
                          <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-emerald-600" />
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {order.items.map((item, idx) => (
                        <span key={idx} className="text-sm bg-stone-100 px-3 py-1.5 rounded-lg font-medium">
                          {item.product_name}: <span className="text-amber-600">{item.quantity}</span>
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t">
                      <span className="font-bold text-xl text-amber-600">€{order.total_with_tax.toFixed(2)}</span>
                      {order.can_edit && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openEditOrderDialog(order)}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        >
                          <Edit className="w-4 h-4 me-1" />
                          تعديل
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Order Dialog - Responsive */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 md:p-6 pb-0 sticky top-0 bg-white z-10 border-b">
            <DialogTitle className="text-xl md:text-2xl">
              {editingOrder ? 'تعديل الطلب' : 'إنشاء طلب جديد'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-4 md:p-6 space-y-6">
            {/* Date */}
            <div className="space-y-2">
              <Label className="text-base font-bold">تاريخ التوصيل</Label>
              <Input
                type="date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                disabled={!!editingOrder}
                className="h-12 text-lg"
              />
            </div>
            
            {/* Products Grid */}
            <div className="space-y-4">
              <Label className="text-base font-bold">المنتجات</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {products.map((product) => {
                  const item = orderItems.find(i => i.product_id === product.id);
                  const quantity = item?.quantity || 0;
                  
                  return (
                    <Card key={product.id} className={`overflow-hidden transition-all ${quantity > 0 ? 'ring-2 ring-amber-500 bg-amber-50' : ''}`}>
                      <CardContent className="p-4">
                        {/* Product Header */}
                        <div className="flex items-center gap-3 mb-4">
                          {product.image_base64 ? (
                            <img 
                              src={product.image_base64} 
                              alt={product.name}
                              className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-xl"
                            />
                          ) : (
                            <div className="w-16 h-16 md:w-20 md:h-20 bg-stone-200 rounded-xl flex items-center justify-center">
                              <Package className="w-8 h-8 text-stone-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-bold text-lg">{product.name}</p>
                            <p className="text-amber-600 font-bold text-xl">€{product.price.toFixed(2)}</p>
                          </div>
                        </div>
                        
                        {/* Quick Selection Buttons */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {quickAmounts.map((amount) => (
                            <Button
                              key={amount}
                              type="button"
                              variant={quantity === amount ? "default" : "outline"}
                              size="sm"
                              className={`flex-1 min-w-[50px] h-10 text-base font-bold ${
                                quantity === amount ? 'bg-amber-600 hover:bg-amber-700' : ''
                              }`}
                              onClick={() => updateQuantity(product.id, amount)}
                            >
                              {amount}
                            </Button>
                          ))}
                        </div>
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center justify-center gap-2 bg-white p-2 rounded-xl border">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-12 w-12 p-0 text-2xl font-bold"
                            onClick={() => updateQuantity(product.id, quantity - 1)}
                          >
                            <Minus className="w-5 h-5" />
                          </Button>
                          <Input
                            type="number"
                            min="0"
                            value={quantity}
                            onChange={(e) => updateQuantity(product.id, parseInt(e.target.value) || 0)}
                            className="w-24 h-12 text-center text-2xl font-bold"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            className="h-12 w-12 p-0 text-2xl font-bold"
                            onClick={() => updateQuantity(product.id, quantity + 1)}
                          >
                            <Plus className="w-5 h-5" />
                          </Button>
                        </div>
                        
                        {/* Item Total */}
                        {quantity > 0 && (
                          <div className="mt-3 text-center text-lg font-bold text-amber-600">
                            = €{(product.price * quantity).toFixed(2)}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-base font-bold">ملاحظات (اختياري)</Label>
              <Textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="أي ملاحظات إضافية..."
                rows={3}
                className="text-base"
              />
            </div>
            
            {/* Total */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 md:p-6 rounded-xl text-white">
              <div className="flex justify-between items-center">
                <span className="font-bold text-lg">الإجمالي</span>
                <span className="text-3xl md:text-4xl font-bold">
                  €{calculateOrderTotal().toFixed(2)}
                </span>
              </div>
            </div>
            
            {/* Edit Warning */}
            {editingOrder && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-amber-800">تنبيه: يمكنك التعديل مرة واحدة فقط</p>
                  <p className="text-amber-700 text-sm">بعد التعديل لن تتمكن من تعديل الطلب مرة أخرى إلا بالاتصال بالإدارة.</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="p-4 md:p-6 pt-0 sticky bottom-0 bg-white border-t gap-3">
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)} className="flex-1 md:flex-none h-12">
              إلغاء
            </Button>
            <Button onClick={handleSaveOrder} className="flex-1 md:flex-none h-12 bg-emerald-600 hover:bg-emerald-700 text-lg font-bold">
              {editingOrder ? 'حفظ التعديل' : 'إرسال الطلب'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortalPage;
